import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { API_URL } from "./config";
import { getCampusFromToken } from "./commonfunctions/useAuthHelpers";

const WalletPosPage = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const [campusCode] = useState(getCampusFromToken());
  const [deviceCode, setDeviceCode] = useState(localStorage.getItem("walletPosDeviceCode") || "");
  const [deviceSecret, setDeviceSecret] = useState(localStorage.getItem("walletPosDeviceSecret") || "");
  const [qrToken, setQrToken] = useState("");
  const [resolveInfo, setResolveInfo] = useState(null);
  const [form, setForm] = useState({
    grossAmount: "",
    discountAmount: "0",
    remarks: "",
    itemName: "",
  });
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [scannerMode, setScannerMode] = useState("");
  const [cameraError, setCameraError] = useState("");

  const nativeVideoRef = useRef(null);
  const nativeStreamRef = useRef(null);
  const nativeLoopRef = useRef(null);
  const html5QrRef = useRef(null);
  const lastScanRef = useRef("");
  const lastScanAtRef = useRef(0);
  const scannerDivId = "wallet-pos-html5-qrcode";

  const hasMedia = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const hasBarcodeDetector = typeof window !== "undefined" && "BarcodeDetector" in window;
  const isSecure = typeof window !== "undefined" && (window.isSecureContext || window.location.hostname === "localhost");


  useEffect(() => {
    if (!cameraOpen) return;
  
    let cancelled = false;
  
    const bootScanner = async () => {
      try {
        const el = document.getElementById(scannerDivId);
        if (!el) {
          throw new Error("Scanner container not rendered");
        }
  
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
  
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras || !cameras.length) {
          throw new Error("No camera found on this device");
        }
  
        let chosenCameraId = cameras[0].id;
        const rearLike = cameras.find((c) =>
          /back|rear|environment/i.test(`${c.label || ""}`)
        );
        if (rearLike) chosenCameraId = rearLike.id;
  
        const html5Qr = new Html5Qrcode(scannerDivId, { verbose: false });
        html5QrRef.current = html5Qr;
  
        await html5Qr.start(
          chosenCameraId,
          {
            fps: 10,
            qrbox: { width: 240, height: 240 },
            aspectRatio: 1.333334,
          },
          async (decodedText) => {
            await handleDetectedCode(decodedText);
          },
          () => {}
        );
  
        if (!cancelled) {
          setCameraLoading(false);
        }
      } catch (err) {
        console.error("Camera open failed:", err);
        if (!cancelled) {
          setCameraError(err?.message || "Failed to open camera");
          setCameraLoading(false);
        }
      }
    };
  
    const t = setTimeout(bootScanner, 50);
  
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [cameraOpen]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDevice = () => {
    localStorage.setItem("walletPosDeviceCode", deviceCode);
    localStorage.setItem("walletPosDeviceSecret", deviceSecret);
    setMsg({ type: "success", text: "POS device credentials saved locally on this machine" });
  };

  const resolveQr = async (tokenOverride = null) => {
    try {
      const tokenToUse = String(tokenOverride ?? qrToken ?? "").trim();

      if (!tokenToUse) {
        setMsg({ type: "error", text: "Please scan or enter a QR token" });
        return;
      }

      const res = await axios.post(
        `${API_URL}/api/wallet/device/resolve-qr`,
        { qrToken: tokenToUse },
        {
          headers: {
            "x-device-code": deviceCode,
            "x-device-secret": deviceSecret,
          },
        }
      );

      setQrToken(tokenToUse);
      setResolveInfo(res.data || null);
      setMsg({ type: "success", text: "QR resolved successfully" });
    } catch (err) {
      setResolveInfo(null);
      setMsg({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to resolve QR",
      });
    }
  };

  const submitSale = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/wallet/device/pos-sale`,
        {
          qrToken: String(qrToken || "").trim(),
          grossAmount: Number(form.grossAmount),
          discountAmount: Number(form.discountAmount || 0),
          remarks: form.remarks,
          items: form.itemName
            ? [
                {
                  itemName: form.itemName,
                  qty: 1,
                  rate: Number(form.grossAmount),
                  amount: Number(form.grossAmount),
                },
              ]
            : [],
        },
        {
          headers: {
            "x-device-code": deviceCode,
            "x-device-secret": deviceSecret,
          },
        }
      );

      setMsg({
        type: "success",
        text: `POS sale successful. Balance after: ₹${Number(
          res.data?.BalanceAfter || 0
        ).toFixed(2)}`,
      });

      await resolveQr(String(qrToken || "").trim());

      setForm({
        grossAmount: "",
        discountAmount: "0",
        remarks: "",
        itemName: "",
      });
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "POS sale failed",
      });
    }
  };

  const handleDetectedCode = async (rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return;
  
    const now = Date.now();
    if (lastScanRef.current === value && now - lastScanAtRef.current < 2000) {
      return;
    }
  
    lastScanRef.current = value;
    lastScanAtRef.current = now;
  
    setQrToken(value);
    await resolveQr(value);
    await stopCamera();
  };

  const stopNativeCamera = () => {
    if (nativeLoopRef.current) {
      cancelAnimationFrame(nativeLoopRef.current);
      nativeLoopRef.current = null;
    }
    if (nativeStreamRef.current) {
      nativeStreamRef.current.getTracks().forEach((track) => track.stop());
      nativeStreamRef.current = null;
    }
    if (nativeVideoRef.current) {
      nativeVideoRef.current.srcObject = null;
    }
  };

  const stopHtml5Qr = async () => {
    try {
      if (html5QrRef.current) {
        const state = html5QrRef.current.getState?.();
        if (state === 2 || state === 3) {
          await html5QrRef.current.stop();
        }
        await html5QrRef.current.clear();
        html5QrRef.current = null;
      }
    } catch (err) {
      console.error("Failed to stop html5-qrcode:", err);
    }
  };

  const stopCamera = async () => {
    try {
      if (html5QrRef.current) {
        const state = html5QrRef.current.getState?.();
        if (state === 2 || state === 3) {
          await html5QrRef.current.stop();
        }
        await html5QrRef.current.clear();
        html5QrRef.current = null;
      }
    } catch (err) {
      console.error("Failed to stop camera:", err);
    } finally {
      setCameraOpen(false);
      setCameraLoading(false);
    }
  };

  const startNativeBarcodeDetector = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });
  
    nativeStreamRef.current = stream;
    setCameraOpen(true);
    setScannerMode("native");
  
    const video = nativeVideoRef.current;
    if (!video) {
      throw new Error("Video element not found");
    }
  
    video.srcObject = stream;
    video.setAttribute("playsinline", true);
    video.muted = true;
  
    await video.play();
  
    // wait until video has real frame data
    await new Promise((resolve, reject) => {
      const done = () => {
        cleanup();
        resolve();
      };
      const fail = () => {
        cleanup();
        reject(new Error("Video failed to load camera stream"));
      };
      const cleanup = () => {
        video.removeEventListener("loadedmetadata", done);
        video.removeEventListener("loadeddata", done);
        video.removeEventListener("canplay", done);
        video.removeEventListener("error", fail);
      };
  
      if (
        video.readyState >= 2 &&
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        resolve();
        return;
      }
  
      video.addEventListener("loadedmetadata", done, { once: true });
      video.addEventListener("loadeddata", done, { once: true });
      video.addEventListener("canplay", done, { once: true });
      video.addEventListener("error", fail, { once: true });
    });
  
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
  
    const scanFrame = async () => {
      const v = nativeVideoRef.current;
  
      if (!v || !nativeStreamRef.current) return;
  
      // skip until there is a usable frame
      if (
        v.readyState < 2 ||
        v.videoWidth === 0 ||
        v.videoHeight === 0 ||
        v.paused ||
        v.ended
      ) {
        nativeLoopRef.current = requestAnimationFrame(scanFrame);
        return;
      }
  
      try {
        const barcodes = await detector.detect(v);
        if (barcodes?.length && barcodes[0]?.rawValue) {
          await handleDetectedCode(barcodes[0].rawValue);
          return;
        }
      } catch (err) {
        // Ignore transient invalid-state errors and continue scanning
        if (err?.name !== "InvalidStateError") {
          console.error("Native barcode detect failed:", err);
        }
      }
  
      nativeLoopRef.current = requestAnimationFrame(scanFrame);
    };
  
    nativeLoopRef.current = requestAnimationFrame(scanFrame);
  };

  const startHtml5QrFallback = async () => {
    setCameraOpen(true);
    setScannerMode("html5-qrcode");
  
    const html5Qr = new Html5Qrcode(scannerDivId, {
      verbose: false,
    });
    html5QrRef.current = html5Qr;
  
    const cameras = await Html5Qrcode.getCameras();
    if (!cameras || !cameras.length) {
      throw new Error("No camera found on this device");
    }
  
    let chosenCameraId = cameras[0].id;
  
    const rearLike = cameras.find((c) =>
      /back|rear|environment/i.test(`${c.label || ""}`)
    );
    if (rearLike) {
      chosenCameraId = rearLike.id;
    }

    await html5Qr.start(
      chosenCameraId,
      {
        fps: 10,
        qrbox: { width: 240, height: 240 },
        aspectRatio: 1.333334,
      },
      async (decodedText) => {
        await handleDetectedCode(decodedText);
      },
      () => {}
    );
  };

  const startCamera = async () => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setMsg({ type: "error", text: "Camera is not supported in this browser on this device" });
      return;
    }
  
    if (!(window.isSecureContext || window.location.hostname === "localhost")) {
      setMsg({ type: "error", text: "Camera access requires HTTPS or localhost" });
      return;
    }
  
    setCameraError("");
    setCameraLoading(true);
    setCameraOpen(true); // only open UI here
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Wallet POS Scanner
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Scan QR and charge wallet balance
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!msg.text && (
            <Snackbar open autoHideDuration={3000} onClose={() => setMsg({ type: "", text: "" })}>
              <Alert severity={msg.type || "info"} sx={{ borderRadius: 2 }}>
                {msg.text}
              </Alert>
            </Snackbar>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontWeight: 900 }}>Device Setup</Typography>

                  <TextField
                    label="Device Code"
                    value={deviceCode}
                    onChange={(e) => setDeviceCode(e.target.value)}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  />

                  <TextField
                    label="Device Secret"
                    value={deviceSecret}
                    onChange={(e) => setDeviceSecret(e.target.value)}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  />

                  <Button variant="contained" sx={primaryBtnSx} onClick={saveDevice}>
                    Save Device
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontWeight: 900 }}>Scan & Resolve</Typography>

                  <TextField
                    label="Scanned QR Token"
                    value={qrToken}
                    onChange={(e) => setQrToken(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        resolveQr();
                      }
                    }}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button variant="contained" sx={primaryBtnSx} onClick={() => resolveQr()}>
                      Resolve QR
                    </Button>
                  
                    {!cameraOpen ? (
                      <Button
                        variant="outlined"
                        sx={outlinedBtnSx}
                        onClick={startCamera}
                        disabled={cameraLoading}
                      >
                        {cameraLoading ? "Opening Camera..." : "Scan with Camera"}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        sx={outlinedBtnSx}
                        onClick={stopCamera}
                      >
                        Stop Camera
                      </Button>
                    )}
                  </Stack>

                  {cameraOpen && (
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: 800 }}>
                          Camera Scanner
                        </Typography>
                        <Typography sx={{ opacity: 0.8, fontSize: "0.9rem" }}>
                          Hold the QR steadily in front of the camera.
                        </Typography>
                  
                        <Box
                          id={scannerDivId}
                          sx={{
                            width: "100%",
                            minHeight: 320,
                            borderRadius: 2,
                            overflow: "hidden",
                            backgroundColor: "#000",
                            "& video": {
                              width: "100% !important",
                              height: "320px !important",
                              objectFit: "cover",
                              display: "block",
                              borderRadius: "12px",
                            },
                          }}
                        />
                  
                        {!!cameraLoading && (
                          <Typography sx={{ opacity: 0.75 }}>
                            Opening camera...
                          </Typography>
                        )}
                  
                        {!!cameraError && (
                          <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {cameraError}
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}

                {/*
                  {cameraOpen && scannerMode === "native" && (
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: 800 }}>
                          Camera Scanner
                        </Typography>
                        <Typography sx={{ opacity: 0.8, fontSize: "0.9rem" }}>
                          Hold the QR steadily in front of the camera.
                        </Typography>
                        <video
                          ref={nativeVideoRef}
                          muted
                          playsInline
                          autoPlay
                          style={{
                            width: "100%",
                            maxHeight: 320,
                            borderRadius: 12,
                            backgroundColor: "#000",
                            objectFit: "cover",
                          }}
                        />
                      </Stack>
                    </Paper>
                  )}

                  {cameraOpen && scannerMode === "html5-qrcode" && (
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: 800 }}>
                          Camera Scanner
                        </Typography>
                        <Typography sx={{ opacity: 0.8, fontSize: "0.9rem" }}>
                          Hold the QR steadily in front of the camera.
                        </Typography>
                        <Box
                          id={scannerDivId}
                          sx={{
                            width: "100%",
                            minHeight: 320,
                            borderRadius: 2,
                            overflow: "hidden",
                            backgroundColor: "#000",
                            "& video": {
                              width: "100% !important",
                              height: "320px !important",
                              objectFit: "cover",
                              borderRadius: "12px",
                              display: "block",
                            },
                            "& canvas": {
                              display: "none !important",
                            },
                          }}
                        />
                      </Stack>
                    </Paper>
                  )}
                  */}

                  {!!cameraError && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {cameraError}
                    </Alert>
                  )}

                  {!isSecure && (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      Camera access works only on HTTPS or localhost.
                    </Alert>
                  )}
                </Stack>
              </Paper>

              {resolveInfo?.wallet && (
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
                    Wallet Holder
                  </Typography>

                  <Typography>
                    <b>Name:</b>{" "}
                    {resolveInfo.person?.StudentName ||
                      resolveInfo.person?.EmployeeName ||
                      "-"}
                  </Typography>
                  <Typography>
                    <b>Wallet ID:</b> {resolveInfo.wallet.WalletAccountId}
                  </Typography>
                  <Typography>
                    <b>Balance:</b> ₹{Number(resolveInfo.wallet.CurrentBalance || 0).toFixed(2)}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Item Name"
                          value={form.itemName}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, itemName: e.target.value }))
                          }
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Gross Amount"
                          type="number"
                          value={form.grossAmount}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, grossAmount: e.target.value }))
                          }
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <TextField
                          label="Discount"
                          type="number"
                          value={form.discountAmount}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, discountAmount: e.target.value }))
                          }
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Remarks"
                          value={form.remarks}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, remarks: e.target.value }))
                          }
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Button
                    variant="contained"
                    sx={{ ...primaryBtnSx, mt: 2 }}
                    onClick={submitSale}
                  >
                    Charge Wallet
                  </Button>
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletPosPage;